import React, { useMemo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { useImagesLoaded } from '../../hooks/useImagesLoaded';
import queryString from 'query-string';
import ListingShareable from '../ListingShareable';
import RecentlySoldShareable from '../RecentlySoldShareable';
import RecentlySoldAreaStatsShareable from '../RecentlySoldAreaStatsShareable';
import LocalMarketShareable from '../LocalMarketShareable';
import MyListingShareable from '../MyListingShareable';
import DailyShareable from '../DailyShareable';
import Notable from '../Notable';
import ListpackShareable from '../ListpackShareable';
import TalkingPointsShareable from '../TalkingPointsShareable';
import CustomShareable from '../CustomShareable';
import MarketInsights from '../MarketInsights';
import HousingSnapshotShareable from '../HousingSnapshotShareable';
import { COLOR_WHITE, COLOR_STEAK_SAUCE } from '../colors';
import { getPalette } from './themes';
import { formatPhone } from '../../utils';

const ROOT_WIDTH = 1080;
const ROOT_HEIGHT = 1080;
const PIXEL_DENSITY_FACTOR = 2;
const CONTENT_BORDER_RADIUS = 14;
const CONTENT_BOX_SHADOW = '0 0 16px 1px rgba(0,0,0,0.2)';
const COPYRIGHT_YEAR = new Date().getFullYear();
const FOOTER_HEIGHT = 320;
const DEFAULT_SHAREABLE_WIDTH = 900;
const DEFAULT_SHAREABLE_HEIGHT = 600;

const isCustomDailyDimensions = (type, dimensions)=>
    type === 'daily_shareable' &&
    !!dimensions &&
    !(dimensions.w === DEFAULT_SHAREABLE_WIDTH && dimensions.h === DEFAULT_SHAREABLE_HEIGHT);

const useStyles = makeStyles(()=> ({
    root: {
        width: ROOT_WIDTH * PIXEL_DENSITY_FACTOR,
        height: ROOT_HEIGHT * PIXEL_DENSITY_FACTOR,
        position: 'relative',
        fontFamily: ['"LL Circular Pro"', 'Arial', 'Helvetica', 'sans-serif'],
        overflow: 'hidden',
        textRendering: 'optimizeLegibility',
    },
    viewport: {
        display: 'block',
        position: 'relative',
        width: '100%',
        height: '100%',
        zoom: PIXEL_DENSITY_FACTOR,
    },
    spotWrap: {
        margin: 0,
        minHeight: '100%',
        backgroundColor: COLOR_WHITE,
        position: 'relative',
        boxSizing: 'border-box',
        padding: '60px 90px 0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundImage: ({ primaryColor })=> `linear-gradient(
            to bottom,
            ${primaryColor},
            ${primaryColor} 72.5%,
            ${COLOR_WHITE} 72.5%
        )`,
    },
    taglineWrap: {
        textAlign: 'center',
        color: ({ secondaryColor })=> secondaryColor,
        padding: '0 22px',
        minHeight: 72, /* need this to preserve the layout in case there is no tagline */
        maxWidth: 900,
        margin: '0 auto',
    },
    quotes: {
        fontSize: '124px',
        lineHeight: '32px',
    },
    tagline: {
        fontSize: '32px',
        fontWeight: 700,
    },
    shareableWrap: {
        maxWidth: 900,
        margin: '40px auto',
    },
    shareableContainer: {
        width: '100%',
        height: 0,
        paddingBottom: '66.67%',
        borderRadius: CONTENT_BORDER_RADIUS,
        boxShadow: CONTENT_BOX_SHADOW,
    },
    userInfo: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        color: '#1a1a1a',
    },
    headshot: {
        width: 100,
        height: 100,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        borderRadius: '50%',
    },
    contact: {
        marginLeft: 24,
        flexGrow: 1,
    },
    name: {
        fontSize: '32px',
        fontWeight: 700,
    },
    title: {
        fontSize: '22px',
        fontWeight: 700,
    },
    brokerageLicenseNumber: {
        fontSize: '22px',
    },
    email: {
        marginTop: 0,
        fontSize: '22px',
    },
    phoneNumber: {
        fontSize: '22px',
        fontWeight: 700,
        marginLeft: 12,
    },
    website: {
        fontSize: '22px',
    },
    iconPhone: {
        '& path': {
            fill: ({ primaryColor })=> primaryColor,
        },
    },
    realtorLicenseNumber: {
        fontSize: '22px',
    },
    brandingWrapper: {
        marginLeft: 16,
        display: 'flex',
    },
    branding: {
        maxWidth: 186,
        minWidth: 186,
        height: 88,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'contain',
    },
    disclaimer: {
        marginTop: 14,
        color: COLOR_STEAK_SAUCE,
        fontSize: '12px',
        marginLeft: -74,
        marginRight: -74,
        textAlign: 'center',
    },
    premiumWatermark: {
        position: 'absolute',
        left: '50%',
        bottom: 96,
        zIndex: 9999,

        '& img': {
            position: 'relative',
            left: '-50%',
            width: 400,
            height: 400,
        },
    },
    trademarkContent: {
        display: 'flex',
        paddingTop: 10,
    },
    lrLogoWrap: {
        paddingRight: 30,
    },
    copyright: {
        fontSize: '16px',
        color: COLOR_STEAK_SAUCE,
        paddingTop: 5,
    },

    portraitSpot: {
        margin: 0,
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        backgroundColor: ({ primaryColor })=> primaryColor,
        display: 'flex',
        flexDirection: 'column',
    },
    portraitTop: {
        flex: 1,
        minHeight: 0,
        boxSizing: 'border-box',
        padding: '64px 90px 48px',
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    portraitTaglineWrap: {
        marginBottom: 40,
    },
    portraitImageWrap: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
    },
    portraitImage: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        borderRadius: CONTENT_BORDER_RADIUS,
        boxShadow: CONTENT_BOX_SHADOW,
    },
    portraitDisclaimer: {
        marginTop: 16,
        color: ({ secondaryColor })=> secondaryColor,
        fontSize: '12px',
        textAlign: 'center',
        maxWidth: 900,
    },
    portraitFooter: {
        height: FOOTER_HEIGHT,
        minHeight: FOOTER_HEIGHT,
        boxSizing: 'border-box',
        backgroundColor: COLOR_WHITE,
        marginTop: '-100px',
        padding: '24px 64px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    portraitWatermark: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,

        '& img': {
            width: 400,
            height: 400,
        },
    },
}));

function ShareableContent({ type, content, parsedData, currentFrame, onImagesLoaded }) {
    const sharedProps = {
        scaleFactor: 1,
        borderRadius: CONTENT_BORDER_RADIUS,
        onImagesLoaded,
    };

    const shareablesByType = {
        market_insights_shareable: (
            <MarketInsights data={content} {...sharedProps} />
        ),
        local_housing_market_data: (
            <LocalMarketShareable data={content} {...sharedProps} />
        ),
        housing_snapshot: (
            <HousingSnapshotShareable data={content} {...sharedProps} />
        ),
        my_listing_shareable: (
            <MyListingShareable data={content} frame={currentFrame} {...sharedProps} />
        ),
        daily_shareable: (
            <DailyShareable data={content || parsedData} frame={currentFrame} {...sharedProps} />
        ),
        notable: (
            <Notable data={content || parsedData} frame={currentFrame} {...sharedProps} />
        ),
        listpack_shareable: (
            <ListpackShareable data={content || parsedData} frame={currentFrame} {...sharedProps} />
        ),
        talking_points_shareable: (
            <TalkingPointsShareable data={content || parsedData} frame={currentFrame} {...sharedProps} />
        ),
        custom_shareable: (
            <CustomShareable
                data={content || parsedData}
                {...sharedProps}
                borderRadius={CONTENT_BORDER_RADIUS / 2}
                border={`16px solid ${COLOR_WHITE}`}
            />
        ),
    };

    if (shareablesByType[type]) {
        return shareablesByType[type];
    }

    if (content && content.area) {
        return <ListingShareable data={content} frame={currentFrame} {...sharedProps} />;
    }
    if (content && content.stats) {
        return <RecentlySoldAreaStatsShareable data={content} {...sharedProps} />;
    }
    return <RecentlySoldShareable data={content} frame={currentFrame} {...sharedProps} />;
}

export default function BrandedShareable() {
    const location = useLocation();
    const [ contentImagesLoaded, setContentImagesLoaded ] = useState(false);
    let parsedData = null;
    let currentFrame = 1;
    let templateRootZoom = 1;
    try {
        const { data, frame, zoom } = queryString.parse(location.search);
        parsedData = JSON.parse(data);
        currentFrame = parseInt(frame, 10);
        if(zoom && !isNaN(parseFloat(zoom))) templateRootZoom = parseFloat(zoom);
    } catch(e) {
        console.error(e);
    }
    const {
        tagline,
        content,
        theme,
        user,
        watermark,
        type,
        palette,
        shareable_image_url,
        image_dimensions,
    } = parsedData || {};
    const classes = useStyles(getPalette(theme, palette));
    const isPortraitDaily = isCustomDailyDimensions(type, image_dimensions);
    const rootHeight = isPortraitDaily ? image_dimensions.h : ROOT_HEIGHT;
    const rootStyle = { zoom: templateRootZoom, height: rootHeight * PIXEL_DENSITY_FACTOR };
    const  {
        name,
        title,
        headshot,
        phone,
        office_phone,
        email,
        branding,
        website,
        brokerage_license_number,
        realtor_license_number,
        nmls_id,
        nmls_disclaimer,
        trademark,
    } = user || {};
    const formattedPhone = useMemo(()=> formatPhone(phone), [phone]);
    const formattedOfficePhone = useMemo(()=> formatPhone(office_phone), [office_phone]);
    const loadedBgs = useImagesLoaded(
        [headshot, branding, isPortraitDaily ? shareable_image_url : null].filter((v)=> v && v.length)
    );
    const onContentImagesLoaded = ()=> setContentImagesLoaded(true);
    const isReady = isPortraitDaily ? loadedBgs : (loadedBgs && contentImagesLoaded);

    useEffect(() => {
        if(isReady && !window.isLoaded) {
            window.isLoaded = true;
        }
    }, [isReady]);

    // shared contact/branding footer content, reused by both the square and portrait layouts.
    const contactBlock = (
        <>
            {headshot && (<div className={classes.headshot}
                style={{backgroundImage: `url("${headshot}")`}}
            ></div>)}

            <div className={classes.contact}>
                <div className={classes.name}>{name}</div>
                <div className={classes.title}>{title}</div>
                {formattedPhone && (
                    <div>
                        <svg className={classes.iconPhone}
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            >
                            <path
                                d="M3.62 7.79C5.06 10.62 7.38 12.93 10.21 14.38L12.41 12.18C12.68 11.91 13.08 11.82 13.43 11.94C14.55 12.31 15.76 12.51 17 12.51C17.55 12.51 18 12.96 18 13.51V17C18 17.55 17.55 18 17 18C7.61 18 0 10.39 0 1C0 0.45 0.45 0 1 0H4.5C5.05 0 5.5 0.45 5.5 1C5.5 2.25 5.7 3.45 6.07 4.57C6.18 4.92 6.1 5.31 5.82 5.59L3.62 7.79Z"
                            />
                        </svg>
                        <span className={classes.phoneNumber}>{formattedPhone}</span>
                    </div>
                )}

                <div className={classes.email}>{email}</div>

                {website && (<div className={classes.website}>{website}</div>)}

                {realtor_license_number && (<div className={classes.realtorLicenseNumber}>License # {realtor_license_number}</div>)}
                {nmls_id && (<div className={classes.realtorLicenseNumber}>NMLS ID # {nmls_id}</div>)}

                {trademark && (
                    <div className={classes.trademarkContent}>
                        <div className={classes.lrLogoWrap}>
                            <div>
                                <img src="./images/lr-logo-disclaimer-black.svg" alt="LR Logo" />
                            </div>
                            <div className={classes.copyright}>
                                © ListReports {COPYRIGHT_YEAR}, all rights reserved
                            </div>
                        </div>
                        <div className={classes.equalHousingWrap}>
                            <img src="./images/equal-housing-logo-black.svg" alt="Equal Housing Logo" />
                        </div>
                    </div>
                )}
            </div>

            <div className={classes.brandingWrapper} style={{ alignItems: (office_phone || brokerage_license_number) ? 'start' : 'center' }}>
                <div>
                    {branding && (<div className={classes.branding}
                        style={{backgroundImage: `url("${branding}")`}}
                    ></div>)}
                    {formattedOfficePhone && (
                        <div>
                            <svg className={classes.iconPhone}
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                >
                                <path
                                    d="M3.62 7.79C5.06 10.62 7.38 12.93 10.21 14.38L12.41 12.18C12.68 11.91 13.08 11.82 13.43 11.94C14.55 12.31 15.76 12.51 17 12.51C17.55 12.51 18 12.96 18 13.51V17C18 17.55 17.55 18 17 18C7.61 18 0 10.39 0 1C0 0.45 0.45 0 1 0H4.5C5.05 0 5.5 0.45 5.5 1C5.5 2.25 5.7 3.45 6.07 4.57C6.18 4.92 6.1 5.31 5.82 5.59L3.62 7.79Z"
                                />
                            </svg>
                            <span className={classes.phoneNumber}>{formattedOfficePhone}</span>
                        </div>
                    )}
                    {brokerage_license_number && (<div className={classes.brokerageLicenseNumber}>License # {brokerage_license_number}</div>)}
                </div>
            </div>
        </>
    );

    if(isPortraitDaily) {
        return (
            <>
                <link rel="stylesheet" href="./css/ll-circular-pro.css" />
                <div className={classes.root} id="templateRoot" style={rootStyle}>
                    <div className={classes.viewport}>
                        <div className={classes.portraitSpot}>
                            <div className={classes.portraitTop}>
                                <div className={`${classes.taglineWrap} ${classes.portraitTaglineWrap}`}>
                                    {tagline && (<div className={classes.tagline}>{tagline}</div>)}
                                </div>

                                <div className={classes.portraitImageWrap}>
                                    {shareable_image_url && (
                                        <img className={classes.portraitImage} src={shareable_image_url} alt="shareable" />
                                    )}
                                    {watermark && (<div className={classes.portraitWatermark}>
                                        <img src="./images/watermark-stamp.svg" alt="Watermark" />
                                    </div>)}
                                </div>

                                {nmls_disclaimer && (<div className={classes.portraitDisclaimer}>{nmls_disclaimer}</div>)}
                            </div>

                            <div className={classes.portraitFooter}>
                                {contactBlock}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <link rel="stylesheet" href="./css/ll-circular-pro.css" />
            <div className={classes.root} id="templateRoot" style={{zoom: templateRootZoom}}>
                <div className={classes.viewport}>
                    <div className={classes.spotWrap}>

                        <div className={classes.taglineWrap}>
                            {tagline && (
                                <>
                                    {/*
                                        // Removing quotes to avoid user confusion
                                        // https://listreports.atlassian.net/browse/AG-1406
                                        <div className={classes.quotes}>&#8220;</div>
                                    */}
                                    <div className={classes.tagline}>{tagline}</div>
                                </>
                            )}
                        </div>

                        <div className={classes.shareableWrap}>
                            <div className={classes.shareableContainer}>
                                <ShareableContent
                                    type={type}
                                    content={content}
                                    parsedData={parsedData}
                                    currentFrame={currentFrame}
                                    onImagesLoaded={onContentImagesLoaded}
                                />
                            </div>
                        </div>

                        <div className={classes.userInfo}>
                            {contactBlock}
                        </div>


                        {nmls_disclaimer && (<div className={classes.disclaimer}>{nmls_disclaimer}</div>)}


                        {watermark && (<div className={classes.premiumWatermark}>
                            <img src="./images/watermark-stamp.svg" alt="Watermark" />
                        </div>)}
                    </div>
                </div>
            </div>
        </>
    );
}
